import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as blogApi from "../api/blog";
import NewNavbar from "../components/newNavbar";
import {
  Calendar,
  User,
  Eye,
  ArrowLeft,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon,
} from "lucide-react";
import { getTopbarOffers } from "../api/topbar";
import { Helmet } from "react-helmet-async";

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function fetchBlog() {
      setLoading(true);
      setError(null);
      try {
        const response = await blogApi.getBlogBySlug(slug);
        if (response.success && response.data) {
          setBlog(response.data);
        } else {
          setError("Blog not found");
        }
      } catch (err) {
        console.error("Error fetching blog:", err);
        setError("Failed to load blog. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    if (slug) {
      fetchBlog();
    }
  }, [slug]);

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(blog?.title || "");

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], "_blank", "width=600,height=400");
    }
  };

  if (loading) {
    return (
      <>
        <NewNavbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            <p className="mt-4 text-gray-600">Loading blog...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <NewNavbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="text-6xl mb-4">📝</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Blog Not Found
            </h1>
            <p className="text-gray-600 mb-6">
              {error || "The blog you're looking for doesn't exist."}
            </p>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Homepage
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.title} | Ferrea Fashion</title>
        <meta
          name="description"
          content={
            blog.short_description || blog.full_description.substring(0, 160)
          }
        />
        <meta property="og:title" content={blog.title} />
        <meta
          property="og:description"
          content={
            blog.short_description || blog.full_description.substring(0, 160)
          }
        />
        {blog.image && <meta property="og:image" content={blog.image} />}
        <meta property="og:type" content="article" />
      </Helmet>

      <NewNavbar />

      <div className="bg-gray-50 min-h-screen">
        {/* Back Button */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            Back
          </button>
        </div>

        {/* Blog Content */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {/* Blog Header */}
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {blog.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>
                  {new Date(blog.created_at).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>

              {blog.author && (
                <div className="flex items-center gap-2">
                  <User size={16} />
                  <span>{blog.author}</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Eye size={16} />
                <span>{blog.views} views</span>
              </div>

              {blog.category && (
                <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-xs font-medium">
                  {blog.category}
                </span>
              )}
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {blog.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Share Buttons */}
            <div className="relative flex items-center gap-3">
              <button
                onClick={() => setShareOpen(!shareOpen)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Share2 size={18} />
                <span className="font-medium">Share</span>
              </button>

              {shareOpen && (
                <div className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-10 flex items-center gap-2">
                  <button
                    onClick={() => handleShare("facebook")}
                    className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Share on Facebook"
                  >
                    <Facebook size={20} className="text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleShare("twitter")}
                    className="p-2 hover:bg-sky-50 rounded-lg transition-colors"
                    title="Share on Twitter"
                  >
                    <Twitter size={20} className="text-sky-500" />
                  </button>
                  <button
                    onClick={() => handleShare("linkedin")}
                    className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Share on LinkedIn"
                  >
                    <Linkedin size={20} className="text-blue-700" />
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Copy link"
                  >
                    <LinkIcon
                      size={20}
                      className={copied ? "text-green-600" : "text-gray-600"}
                    />
                  </button>
                  {copied && (
                    <span className="text-xs text-green-600 font-medium ml-2">
                      Copied!
                    </span>
                  )}
                </div>
              )}
            </div>
          </header>

          {/* Featured Image */}
          {blog.image && (
            <div className="mb-8 rounded-2xl overflow-hidden shadow-xl">
              <img
                src={blog.image}
                alt={blog.title}
                className="w-full h-auto object-cover max-h-[600px]"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          {/* Blog Content */}
          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
            <div
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-cyan-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-img:rounded-lg prose-img:shadow-md"
              style={{
                fontSize: "1.125rem",
                lineHeight: "1.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {blog.full_description}
            </div>
          </div>

          {/* Bottom Share Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Share this article
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleShare("facebook")}
                  className="p-3 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Share on Facebook"
                >
                  <Facebook size={24} className="text-blue-600" />
                </button>
                <button
                  onClick={() => handleShare("twitter")}
                  className="p-3 hover:bg-sky-50 rounded-lg transition-colors"
                  title="Share on Twitter"
                >
                  <Twitter size={24} className="text-sky-500" />
                </button>
                <button
                  onClick={() => handleShare("linkedin")}
                  className="p-3 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Share on LinkedIn"
                >
                  <Linkedin size={24} className="text-blue-700" />
                </button>
                <button
                  onClick={handleCopyLink}
                  className="p-3 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copy link"
                >
                  <LinkIcon
                    size={24}
                    className={copied ? "text-green-600" : "text-gray-600"}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Back to Blogs Button */}
          <div className="mt-12 text-center">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
              Back to All Blogs
            </button>
          </div>
        </article>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-400">
              © {new Date().getFullYear()} Ferrea Fashion. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default BlogDetail;
